import Decimal from "decimal.js";

export type BudgetAllocationEditKind = "save" | "undo";
export type BudgetAllocationEditStatus =
  | "saving"
  | "saved"
  | "failed"
  | "restored";

export interface BudgetAllocationEdit {
  kind: BudgetAllocationEditKind;
  previousAvailable: string;
  requestedAvailable: string;
  previousMonthlyAssignment: string;
  status: BudgetAllocationEditStatus;
}

export function beginBudgetAllocationEdit(
  previousAvailable: string,
  requestedAvailable: string,
  previousMonthlyAssignment: string,
): BudgetAllocationEdit {
  return {
    kind: "save",
    previousAvailable,
    requestedAvailable,
    previousMonthlyAssignment,
    status: "saving",
  };
}

export function completeBudgetAllocationEdit(
  edit: BudgetAllocationEdit,
  success: boolean,
): BudgetAllocationEdit {
  return {
    ...edit,
    status: success ? (edit.kind === "undo" ? "restored" : "saved") : "failed",
  };
}

export function beginBudgetAllocationUndo(
  edit: BudgetAllocationEdit,
): BudgetAllocationEdit {
  if (edit.status !== "saved") {
    throw new Error("only a saved allocation edit can be undone");
  }

  return {
    kind: "undo",
    previousAvailable: edit.requestedAvailable,
    requestedAvailable: edit.previousAvailable,
    previousMonthlyAssignment: edit.previousMonthlyAssignment,
    status: "saving",
  };
}

export function budgetAllocationEditMatches(
  edit: BudgetAllocationEdit,
  available: string,
  monthlyAssignment: string,
): boolean {
  // Available is not a unique inverse of assignment under parent funding.
  if (edit.kind === "undo") {
    return new Decimal(monthlyAssignment).eq(edit.previousMonthlyAssignment);
  }

  // The funding solver guarantees the displayed cent target, not raw equality.
  return new Decimal(available)
    .toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN)
    .eq(
      new Decimal(edit.requestedAvailable).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN),
    );
}
